/**
 * HTML utility functions for copy formatters
 * These create HTML that matches the visual styling of the renderers
 */

/**
 * Parse basic markdown formatting in text
 * Handles **bold** and *italic* syntax
 */
export function parseMarkdown(text: string): string {
  if (!text) return text;

  let processed = text;

  // Handle **bold** and __bold__
  processed = processed.replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>');
  processed = processed.replace(/__([^_]+)__/g, '<strong>$1</strong>');

  // Handle *italic* and _italic_ (but not if part of bold)
  processed = processed.replace(/(?<!\*)\*([^*\n]+)\*(?!\*)/g, '<em>$1</em>');
  processed = processed.replace(/(?<!_)_([^_\n]+)_(?!_)/g, '<em>$1</em>');

  return processed;
}

/**
 * Parse full markdown with GFM support (similar to react-markdown with remark-gfm)
 * Handles headings, lists, tables, bold, italic, links, code blocks
 */
export function parseMarkdownFull(text: string): string {
  if (!text) return text;

  let html = text;

  // Split by lines for processing
  const lines = html.split('\n');
  const processed: string[] = [];
  let inTable = false;
  let inCodeBlock = false;
  let inList = false;

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    const trimmedLine = line.trim();

    // Code blocks (```)
    if (trimmedLine.startsWith('```')) {
      if (inCodeBlock) {
        processed.push('</pre></code>');
        inCodeBlock = false;
      } else {
        processed.push('<code><pre>');
        inCodeBlock = true;
      }
      continue;
    }

    if (inCodeBlock) {
      processed.push(line);
      continue;
    }

    // Tables (GFM)
    if (trimmedLine.includes('|') && trimmedLine.split('|').length > 2) {
      const cells = trimmedLine
        .split('|')
        .map((c) => c.trim())
        .filter((c) => c);

      // Check if it's a separator row (|---|---|)
      if (cells.every((c) => /^:?-+:?$/.test(c))) {
        continue; // Skip separator row
      }

      if (!inTable) {
        processed.push('<table>');
        processed.push('<thead>');
        processed.push(
          '<tr>' +
            cells.map((c) => `<th><strong>${parseInlineMarkdown(c)}</strong></th>`).join('') +
            '</tr>',
        );
        processed.push('</thead>');
        processed.push('<tbody>');
        inTable = true;
      } else {
        processed.push(
          '<tr>' + cells.map((c) => `<td>${parseInlineMarkdown(c)}</td>`).join('') + '</tr>',
        );
      }
      continue;
    } else if (inTable) {
      processed.push('</tbody>');
      processed.push('</table>');
      inTable = false;
    }

    // Headings
    if (trimmedLine.startsWith('####')) {
      processed.push(`<h4>${parseInlineMarkdown(trimmedLine.substring(4).trim())}</h4>`);
      continue;
    } else if (trimmedLine.startsWith('###')) {
      processed.push(`<h3>${parseInlineMarkdown(trimmedLine.substring(3).trim())}</h3>`);
      continue;
    } else if (trimmedLine.startsWith('##')) {
      processed.push(`<h2>${parseInlineMarkdown(trimmedLine.substring(2).trim())}</h2>`);
      continue;
    } else if (trimmedLine.startsWith('#')) {
      processed.push(`<h1>${parseInlineMarkdown(trimmedLine.substring(1).trim())}</h1>`);
      continue;
    }

    // Unordered lists
    if (trimmedLine.startsWith('- ') || trimmedLine.startsWith('* ')) {
      if (!inList) {
        processed.push('<ul>');
        inList = true;
      }
      processed.push(`<li>${parseInlineMarkdown(trimmedLine.substring(2).trim())}</li>`);
      continue;
    }

    // Ordered lists
    const orderedListMatch = trimmedLine.match(/^(\d+)\.\s+(.+)$/);
    if (orderedListMatch) {
      if (!inList) {
        processed.push('<ol>');
        inList = true;
      }
      processed.push(`<li>${parseInlineMarkdown(orderedListMatch[2])}</li>`);
      continue;
    }

    // Close list if we were in one
    if (
      inList &&
      !trimmedLine.startsWith('- ') &&
      !trimmedLine.startsWith('* ') &&
      !orderedListMatch
    ) {
      processed.push(inList && trimmedLine.match(/^\d+\./) ? '</ol>' : '</ul>');
      inList = false;
    }

    // Empty lines
    if (!trimmedLine) {
      processed.push('<br/>');
      continue;
    }

    // Regular paragraphs
    processed.push(`<p>${parseInlineMarkdown(trimmedLine)}</p>`);
  }

  // Close any open tags
  if (inTable) {
    processed.push('</tbody>');
    processed.push('</table>');
  }
  if (inList) {
    processed.push('</ul>');
  }
  if (inCodeBlock) {
    processed.push('</pre></code>');
  }

  return processed.join('\n');
}

/**
 * Parse inline markdown (bold, italic, links, inline code)
 */
function parseInlineMarkdown(text: string): string {
  let processed = text;

  // Inline code `code`
  processed = processed.replace(/`([^`]+)`/g, '<code>$1</code>');

  // Links [text](url)
  processed = processed.replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2">$1</a>');

  // Bold **text** or __text__
  processed = processed.replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>');
  processed = processed.replace(/__([^_]+)__/g, '<strong>$1</strong>');

  // Italic *text* or _text_ (but not if part of bold)
  processed = processed.replace(/(?<!\*)\*([^*\n]+)\*(?!\*)/g, '<em>$1</em>');
  processed = processed.replace(/(?<!_)_([^_\n]+)_(?!_)/g, '<em>$1</em>');

  return processed;
}

/**
 * Create an HTML heading (no background colors for Google Sheets compatibility)
 */
export function h1(text: string): string {
  return `<h1>${text}</h1>`;
}

export function h2(text: string): string {
  return `<h2>${text}</h2>`;
}

export function h3(text: string): string {
  return `<h3>${text}</h3>`;
}

export function h4(text: string): string {
  return `<h4>${text}</h4>`;
}

/**
 * Create a paragraph
 */
export function p(text: string): string {
  return `<p>${text}</p>`;
}

/**
 * Create bold text
 */
export function strong(text: string): string {
  return `<strong>${text}</strong>`;
}

/**
 * Create italic text
 */
export function em(text: string): string {
  return `<em>${text}</em>`;
}

/**
 * Create a bullet list
 */
export function ul(items: string[]): string {
  const listItems = items.map((item) => `<li>${item}</li>`).join('');
  return `<ul>${listItems}</ul>`;
}

/**
 * Create a numbered list
 */
export function ol(items: string[]): string {
  const listItems = items.map((item) => `<li>${item}</li>`).join('');
  return `<ol>${listItems}</ol>`;
}

/**
 * Create a table (clean, no background colors for Google Sheets)
 */
export function table(headers: string[], rows: string[][]): string {
  const headerRow = `<tr>${headers.map((h) => `<th><strong>${h}</strong></th>`).join('')}</tr>`;

  const bodyRows = rows
    .map((row) => `<tr>${row.map((cell) => `<td>${cell}</td>`).join('')}</tr>`)
    .join('');

  return `<table>${headerRow}${bodyRows}</table>`;
}

/**
 * Create a horizontal rule
 */
export function hr(): string {
  return `<hr />`;
}

/**
 * Create a blockquote
 */
export function blockquote(text: string): string {
  return `<blockquote><em>${text}</em></blockquote>`;
}

/**
 * Create inline code
 */
export function code(text: string): string {
  return `<code>${text}</code>`;
}

/**
 * Create a pre-formatted text block
 */
export function pre(text: string): string {
  return `<pre>${text}</pre>`;
}

/**
 * Create a badge/tag (just text, no background for Sheets compatibility)
 */
export function badge(
  text: string,
  color: 'blue' | 'green' | 'yellow' | 'red' | 'gray' = 'blue',
): string {
  // Just return the text - Google Sheets doesn't need the visual styling
  return text;
}

/**
 * Create a div container (no inline styles)
 */
export function div(content: string, styles?: string): string {
  // Ignore styles parameter for Google Sheets compatibility
  return `<div>${content}</div>`;
}

/**
 * Join HTML sections with line breaks
 */
export function join(...sections: (string | undefined | null)[]): string {
  return sections.filter(Boolean).join('<br/>');
}

/**
 * Wrap content in an HTML document with explicit text color for Google Docs compatibility
 */
export function wrapHTML(content: string): string {
  return `<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8">
  <style>
    body { color: #333; font-family: Arial, sans-serif; }
    h1, h2, h3, h4, h5, h6 { color: #1a1a1a; }
    p, li, td, th { color: #333; }
    blockquote { color: #555; }
    a { color: #0066cc; }
  </style>
</head>
<body style="color: #333;">
${content}
</body>
</html>`;
}
